import { Button, TextInput, useMutationWithToast } from "@repo/ui";
import { useForm } from "react-hook-form";
import {
  EnvelopeIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { AuthenticationService } from "../../services/index";
import { useState } from "react";

type ForgotPasswordFormType = { email: string };

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

export function ForgotPassword() {
  const forgotPasswordForm = useForm<ForgotPasswordFormType>({
    mode: "onBlur",
  });
  const forgotPasswordMutation = useMutationWithToast(
    AuthenticationService.forgotPassword,
  );
  const [isEmailSent, setIsEmailSent] = useState(false);

  const onSubmit = forgotPasswordForm.handleSubmit((formData) => {
    forgotPasswordMutation.mutate(formData, {
      onSuccess() {
        setIsEmailSent(true);
      },
    });
  });

  if (isEmailSent) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <motion.div {...fadeUp} className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10 text-center">
            <div className="w-16 h-16 bg-success-light rounded-full flex items-center justify-center mb-6 mx-auto">
              <CheckCircleIcon className="h-8 w-8 text-success-main" />
            </div>
            <h2
              className="font-bold text-gray-900 mb-2"
              style={{ fontSize: "1.6rem" }}
            >
              Check your inbox
            </h2>
            <p className="text-gray-500 text-md mb-2">
              We've sent a password reset link to your email. Follow the
              instructions to reset your password.
            </p>
            <p className="text-gray-400 text-sm mb-8">
              Didn't receive it? Check your spam folder.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => setIsEmailSent(false)}
                variant="outlined"
                className="w-full"
              >
                Try Again
              </Button>
              <Link to="/login" className="w-full">
                <Button variant="text" className="w-full">
                  <span className="flex items-center justify-center gap-2">
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Login
                  </span>
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <motion.div {...fadeUp} className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10">
          <div className="mb-8">
            <h2
              className="font-bold text-gray-900 mb-1"
              style={{ fontSize: "1.6rem" }}
            >
              Forgot password?
            </h2>
            <p className="text-gray-500 text-md">
              Enter your email and we'll send you a reset link.
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <TextInput
              label="Email address"
              placeholder="you@example.com"
              fullWidth
              control={forgotPasswordForm.control}
              name="email"
              type="email"
              startAdornment={<EnvelopeIcon className="h-5 w-5" />}
            />

            <Button
              loading={forgotPasswordMutation.isPending}
              type="submit"
              className="w-full"
            >
              Send Reset Link
            </Button>
          </form>

          <div className="mt-4">
            <Link to="/login">
              <Button variant="text" className="w-full">
                <span className="flex items-center justify-center gap-2">
                  <ArrowLeftIcon className="h-4 w-4" />
                  Back to Login
                </span>
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
